import { ParsedAddress } from '@/types/landlord.types'

export interface WizardStep {
  isValid: boolean;
  title: string;
  route: string;
  completed: boolean;
}

export interface Step {
  step: number;
  title: string;
  route: string;
  completed: boolean;
}

export enum OrganisationProperty {
  CompanyCode = 'companyCode',
  OrganisationCodeDirty = 'organisationCodeDirty',
  CompanyName = 'companyName',
  CompanyAuthorizedSignatory = 'companyAuthorizedSignatory',
  KoANumber = 'koANumber',
  ValidationIsOngoing = 'validationIsOngoing'
}

export enum ServicesProperty {
  OrderType = 'orderType',
  OtherOrderType = 'otherOrderType',
  ActivationType = 'activationType'
}

export enum PropertyFormFields {
  StreetName = 'streetName',
  StreetNumber = 'streetNumber',
  ApartmentNumber = 'apartmentNumber',
  Entrance = 'entrance',
  City = 'city',
  TenantsName = 'tenantsName',
  TenantsPhone = 'tenantsPhone',
  SwitchLocation = 'switchLocation',
  Bridge = 'bridge',
  Address = 'address'
}

export interface FormFields {
  [key: string]: string | undefined;

  [PropertyFormFields.StreetName]?: string | undefined;
  [PropertyFormFields.StreetNumber]?: string | undefined;
  [PropertyFormFields.ApartmentNumber]?: string | undefined;
  [PropertyFormFields.Entrance]?: string | undefined;
  [PropertyFormFields.City]?: string | undefined;
  [PropertyFormFields.TenantsName]?: string | undefined;
  [PropertyFormFields.TenantsPhone]?: string | undefined;
  [PropertyFormFields.SwitchLocation]?: string | undefined;
  [PropertyFormFields.Bridge]?: string | undefined;
}

export type FormId = number

export interface PropertyForm {
  id: FormId;
  fields: FormFields;
  valid: boolean;
}

export enum ContactOptions {
  Same = 'Samma som beställare',
  Other = 'Annan än beställare, fyll i nedan'
}

export enum ExistingPropertyNetworkOptions {
  Yes = 'Ja',
  NoOurs = 'Nej, vi bygger själva',
  NoTelia = 'Nej, vi önskar beställa av Telia'
}

export enum ContactAssemblyOptions {
  Mounted = 'MC finns redan monterad',
  Self = 'Skicka MC, vi monterar den själva',
  Telia = 'Skicka MC, monteras av Telia'
}

export enum ContactFormProperty {
  Name = 'name',
  MobileNumber = 'mobileNumber',
  Email = 'email',

  ExistingPropertyNetworkOption = 'existingPropertyNetworkOption',

  TechnicianName = 'technicianName',
  TechnicianMobileNumber = 'technicianMobileNumber',
  TechnicianEmail = 'technicianEmail',

  DeliveryName = 'deliveryName',
  DeliveryMobileNumber = 'deliveryMobileNumber',
  DeliveryStreetName = 'deliveryStreetName',
  DeliveryStreetNumber = 'deliveryStreetNumber',
  DeliveryEntrance = 'deliveryEntrance',
  DeliveryCity = 'deliveryCity',
  DeliveryAddress = 'deliveryAddress',

  MountingTechnicianName = 'mountingTechnicianName',
  MountingTechnicianMobileNumber = 'mountingTechnicianMobileNumber',

  TechnicalDeliveryName = 'technicalDeliveryName',
  TechnicalDeliveryMobileNumber = 'technicalDeliveryMobileNumber',
  TechnicalDeliveryStreetName = 'technicalDeliveryStreetName',
  TechnicalDeliveryStreetNumber = 'technicalDeliveryStreetNumber',
  TechnicalDeliveryEntrance = 'technicalDeliveryEntrance',
  TechnicalDeliveryCity = 'technicalDeliveryCity',
  TechnicalDeliveryAddress = 'technicalDeliveryAddress',

  DeliveryDate = 'deliveryDate',
  OtherInformation = 'otherInformation'
}

export interface UnifiedFormFields extends Partial<FormFields>, Partial<ContactFormState> {}

export interface ContactFormState {
  [key: string]: string | undefined;

  [ContactFormProperty.Name]?: string | undefined;
  [ContactFormProperty.MobileNumber]?: string | undefined;
  [ContactFormProperty.Email]?: string | undefined;

  [ContactFormProperty.TechnicianName]?: string | undefined;
  [ContactFormProperty.TechnicianMobileNumber]?: string | undefined;
  [ContactFormProperty.TechnicianEmail]?: string | undefined;

  [ContactFormProperty.DeliveryName]?: string | undefined;
  [ContactFormProperty.DeliveryMobileNumber]?: string | undefined;
  [ContactFormProperty.DeliveryStreetName]?: string | undefined;
  [ContactFormProperty.DeliveryStreetNumber]?: string | undefined;
  [ContactFormProperty.DeliveryEntrance]?: string | undefined;
  [ContactFormProperty.DeliveryCity]?: string | undefined;

  [ContactFormProperty.MountingTechnicianName]?: string | undefined;
  [ContactFormProperty.MountingTechnicianMobileNumber]?: string | undefined;

  [ContactFormProperty.TechnicalDeliveryName]?: string | undefined;
  [ContactFormProperty.TechnicalDeliveryMobileNumber]?: string | undefined;
  [ContactFormProperty.TechnicalDeliveryStreetName]?: string | undefined;
  [ContactFormProperty.TechnicalDeliveryStreetNumber]?: string | undefined;
  [ContactFormProperty.TechnicalDeliveryEntrance]?: string | undefined;
  [ContactFormProperty.TechnicalDeliveryCity]?: string | undefined;

  [ContactFormProperty.DeliveryDate]?: string | undefined;
  [ContactFormProperty.OtherInformation]?: string | undefined;
}

export interface OrderFormFields {
  [OrganisationProperty.CompanyCode]: string;
  [OrganisationProperty.CompanyName]: string;
  [OrganisationProperty.CompanyAuthorizedSignatory]: boolean;
  [OrganisationProperty.KoANumber]?: string;

  [ServicesProperty.OrderType]: string;
  [ServicesProperty.OtherOrderType]?: string;
  [ServicesProperty.ActivationType]?: string;

  propertyForms?: FormFields[];
  propertyAttachments?: ParsedAttachment[];

  contactOption: ContactOptions;
  contactAssemblyOption?: ContactAssemblyOptions;
  contactFormFields: ContactFormState;
}

interface ParsedAttachment {
  sheetName: string;
  addresses: ParsedAddressSpreadsheetData[];
}

export interface Attachment extends ParsedAddressSpreadsheet {
  file: File;
  corrupt: boolean;
  loading: boolean;
  empty: boolean;
  tooManyAddresses: boolean;
}

export interface AddressValidationRequest {
  [ParsedAddress.Street]: string;
  [ParsedAddress.StreetNumber]: string;
  [ParsedAddress.City]: string;
}

export interface ParsedAddressSpreadsheetData extends AddressValidationRequest {
  [ParsedAddress.RowIndex]: number;
  [ParsedAddress.ApartmentNumber]: string;
  [ParsedAddress.Entrance]: string;
  [ParsedAddress.FloorPlan]: string;
  [ParsedAddress.TenantsName]: string;
  [ParsedAddress.TenantsPhone]: string;
  [ParsedAddress.SwitchLocation]: string;
}

export interface ParsedAddressSpreadsheet {
  fileName: string;
  invalidRows: number[];
  addresses: ParsedAddressSpreadsheetData[];
}

export const ORDER_PROPERTY_VALIDATION_MESSAGES: Record<string, string> = {
  [PropertyFormFields.StreetName]: 'Vänligen fyll i detta fält',
  [PropertyFormFields.StreetNumber]: 'Vänligen fyll i detta fält',
  [PropertyFormFields.ApartmentNumber]: 'Vänligen fyll i detta fält',
  [PropertyFormFields.City]: 'Vänligen fyll i detta fält',
  [PropertyFormFields.TenantsPhone]: 'Vänligen ange telefonnummer med landsnummer ex. +46',
  [PropertyFormFields.Bridge]: 'Vänligen fyll i detta fält',
  [PropertyFormFields.Address]: 'Kontrollera om gatunamn, gatunummer och ort är korrekta',
  attachments: 'Bifogad fil överstiger maximal filstorlek (25 MB)'
}

export enum OrganisationFieldLabel {
  CompanyCode = 'Organisationsnummer',
  CompanyName = 'BRF / Förening / Företag',
  CompanyAuthorizedSignatory = 'Jag är firmatecknare / behörig beställare för organisationsnumret beställningen avser',
  KoANumber = 'KO-nummer/A-nummer'
}

export enum ServicesFieldLabel {
  OrderType = 'Vad avser beställningen?',
  OtherOrderType = 'Specificera typ av beställning:',
  ActivationType = 'Tjänst som ska aktiveras',
}

export enum PropertyFieldLabel {
  Address = 'Adress',
  TechnicalInformation = 'Teknisk information',

  StreetName = 'Anslutningsadress',
  StreetNumber = 'Gatunummer',
  ApartmentNumber = 'Lägenhetnummer',
  Entrance = 'Ingång',
  City = 'Ort',
  TenantsName = 'Namn, boende',
  TenantsPhone = 'Mobil, boende',
  SwitchLocation = 'Switchens placering (ej obligatoriskt)',
  Bridge = 'Nätbrygga',
  Attachments = 'Bifoga ev. adresser som fil'
}

export enum ContactFieldLabel {
  ContactOptionSame = 'Samma som beställare',
  ContactOptionOther = 'Annan än beställare, fyll i nedan',

  ContactPersonDetails = 'Kontaktperson beställare',
  TechnicianDetails = 'Teknisk kontakt',
  DeliveryAddressDetails = 'Leveransadress',
  AssemblyOptionDetails = 'Mediakonverter (MC)',
  ExistingPropertyNetworkDetails = 'Finns befintligt fastighetsnät (nät mellan switch och lägenhet/lokal)',
  TechnicianDeliveryDetails = 'Leveransadress MC och eventuell SFP',
  MountingTechnicianDetails = 'Kontaktperson till tekniker för montering av MC',
  TechnicalDeliveryDetails = 'Leveransadress MC och eventuell SFP',

  Name = 'Namn',
  MobileNumber = 'Mobilnummer',
  Email = 'E-post',
  StreetName = 'Leveransadress',

  DeliveryDate = 'Leveransdatum',
  OtherInformation = 'Övrig information',
}

export enum PropertyAddressFieldNames {
  StreetName = 'street-name',
  StreetNumber = 'street-number',
  Entrance = 'entrance',
  City = 'city',
  ApartmentNumber = 'apartment-number'
}
